/** @jsx jsx */
import { useCallback, useMemo } from 'react';
import type { Environment } from 'react-relay';
import {
	JIRA_PROJECT_TYPE_CORE_PROJECT,
	type JiraProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { summaryDataResource } from '@atlassian/jira-router-resources-business-summary-data/src/index.tsx';
import { activityFeedResource } from '@atlassian/jira-router-resources-business-summary-activity-feed/src/index.tsx';
import {
	BoundaryIds,
	overviewCountBoundaryIds,
	type OverviewCountType,
} from '../../common/types.tsx';
import {
	MediumContactSupportWarning,
	MediumRetryWarning,
	SmallContactSupportWarning,
	SmallRetryWarning,
} from '../../common/ui/error/index.tsx';
import {
	projectSummaryDataSelector,
	priorityBreakdownDataSelector,
	statusOverviewDataSelector,
	teamWorkloadDataSelector,
	typesOfWorkDataSelector,
	fetchProjectSummaryData,
	fetchActivityFeedData,
	fetchEpicProgressData,
	fetchPriorityBreakdownData,
	fetchStatusOverviewData,
	fetchTeamWorkloadData,
	fetchTypesOfWorkData,
	fetchRelatedProjectsData,
	fetchFastActivityFeedData,
	fetchCsatOverTimeData,
} from '../../services/index.tsx';
import { withBoundary } from '../avp-bridge/with-boundary/index.tsx';
import { withSummaryData, withRouteResourceData } from '../avp-bridge/with-data/index.tsx';
import {
	withPaginatedSummaryData,
	withRouteResourceAndPaginatedData,
} from '../avp-bridge/with-paginated-data/index.tsx';
import { skeletons } from '../../common/ui/loading/index.tsx';
import { EpicProgress } from './epic-progress/index.tsx';
import { OverviewCount } from './overview-count/index.tsx';
import { PriorityBreakdown } from './priority-breakdown/index.tsx';
import { RecentActivity } from './recent-activity/index.tsx';
import { StatusOverview } from './status-overview/index.tsx';
import { TeamWorkload } from './team-workload/index.tsx';
import { TypesOfWork } from './types-of-work/index.tsx';
import { ActivityFeed } from './activity-feed/index.tsx';
import { RelatedProjects } from './related-projects/index.tsx';
import { CsatOverTime } from './csat-over-time/index.tsx';

export const useOverviewCountWithRouteResourceData = (type: OverviewCountType) => {
	const boundaryId = overviewCountBoundaryIds[type];

	return useMemo(
		() =>
			withBoundary({
				Component: withRouteResourceData({
					Component: OverviewCount,
					routeResource: summaryDataResource,
					dataSelector: projectSummaryDataSelector(type),
					FallbackComponent: SmallRetryWarning,
				}),
				boundaryId,
				Fallback: SmallContactSupportWarning,
				Skeleton: skeletons.OverviewCount,
			}),
		[boundaryId, type],
	);
};

export const usePriorityBreakdownWithRouteResourceData = () =>
	useMemo(
		() =>
			withBoundary({
				Component: withRouteResourceData({
					Component: PriorityBreakdown,
					routeResource: summaryDataResource,
					dataSelector: priorityBreakdownDataSelector,
					FallbackComponent: MediumRetryWarning,
				}),
				boundaryId: BoundaryIds.PriorityBreakdown,
				Fallback: MediumContactSupportWarning,
				Skeleton: skeletons.PriorityBreakdown,
			}),
		[],
	);

export const useStatusOverviewWithRouteResourceData = () =>
	useMemo(
		() =>
			withBoundary({
				Component: withRouteResourceData({
					Component: StatusOverview,
					routeResource: summaryDataResource,
					dataSelector: statusOverviewDataSelector,
					FallbackComponent: SmallRetryWarning,
				}),
				boundaryId: BoundaryIds.StatusOverview,
				Fallback: SmallContactSupportWarning,
				Skeleton: skeletons.StatusOverview,
			}),
		[],
	);

export const useTypesOfWorkWithRouteResourceAndPaginatedData = (
	cloudId: string,
	projectKey: string,
	gqlQuery: string,
) => {
	const fetchTypesOfWorkDataWithProjectKey = useCallback(
		(after: string | null) => fetchTypesOfWorkData(after, cloudId, projectKey, gqlQuery),
		[cloudId, projectKey, gqlQuery],
	);
	return useMemo(
		() =>
			withBoundary({
				Component: withRouteResourceAndPaginatedData({
					Component: TypesOfWork,
					routeResource: summaryDataResource,
					dataSelector: typesOfWorkDataSelector,
					fetchDataFn: fetchTypesOfWorkDataWithProjectKey,
					FallbackComponent: MediumRetryWarning,
				}),
				boundaryId: BoundaryIds.TypesOfWork,
				Fallback: MediumContactSupportWarning,
				Skeleton: skeletons.TypeOfWork,
			}),
		[fetchTypesOfWorkDataWithProjectKey],
	);
};

export const useTeamWorkloadWithRouteResourceAndPaginatedData = (
	cloudId: string,
	projectKey: string,
	gqlQuery: string,
) => {
	const fetchTeamWorkloadDataWithProjectKey = useCallback(
		(after: string | null) => fetchTeamWorkloadData(after, cloudId, projectKey, gqlQuery),
		[cloudId, projectKey, gqlQuery],
	);
	return useMemo(
		() =>
			withBoundary({
				Component: withRouteResourceAndPaginatedData({
					Component: TeamWorkload,
					routeResource: summaryDataResource,
					dataSelector: teamWorkloadDataSelector,
					fetchDataFn: fetchTeamWorkloadDataWithProjectKey,
					FallbackComponent: MediumRetryWarning,
				}),
				boundaryId: BoundaryIds.TeamWorkload,
				Fallback: MediumContactSupportWarning,
				Skeleton: skeletons.TeamWorkload,
			}),
		[fetchTeamWorkloadDataWithProjectKey],
	);
};

export const useRecentActivityWithRouteResourceAndPaginatedData = (projectKey: string) => {
	const fetchActivityFeedDataWithProjectKey = useCallback(
		(after: string | null) => fetchActivityFeedData(after, projectKey),
		[projectKey],
	);

	return useMemo(
		() =>
			withBoundary({
				Component: withRouteResourceAndPaginatedData({
					Component: RecentActivity,
					routeResource: activityFeedResource,
					dataSelector: (data) => data,
					fetchDataFn: fetchActivityFeedDataWithProjectKey,
					FallbackComponent: MediumRetryWarning,
					options: {
						fetchMode: 'replace',
					},
				}),
				boundaryId: BoundaryIds.RecentActivity,
				Fallback: MediumContactSupportWarning,
				Skeleton: skeletons.RecentActivity,
			}),

		[fetchActivityFeedDataWithProjectKey],
	);
};

export const useOverviewCountWithData = (
	cloudId: string,
	projectKey: string,
	type: OverviewCountType,
	gqlQuery: string,
) => {
	const boundaryId = overviewCountBoundaryIds[type];

	return useMemo(
		() =>
			withBoundary({
				Component: withSummaryData({
					Component: OverviewCount,
					fetchDataFn: () => fetchProjectSummaryData(cloudId, projectKey, type, gqlQuery),
					FallbackComponent: SmallRetryWarning,
				}),
				boundaryId,
				Fallback: SmallContactSupportWarning,
				Skeleton: skeletons.OverviewCount,
			}),
		[boundaryId, cloudId, projectKey, type, gqlQuery],
	);
};

export const useStatusOverviewWithData = (
	cloudId: string,
	projectKey: string,
	gqlQuery: string,
) => {
	return useMemo(
		() =>
			withBoundary({
				Component: withSummaryData({
					Component: StatusOverview,
					fetchDataFn: () => fetchStatusOverviewData(cloudId, projectKey, gqlQuery),
					FallbackComponent: SmallRetryWarning,
				}),
				boundaryId: BoundaryIds.StatusOverview,
				Fallback: SmallContactSupportWarning,
				Skeleton: skeletons.StatusOverview,
			}),
		[cloudId, projectKey, gqlQuery],
	);
};

export const usePriorityBreakdownWithData = (
	cloudId: string,
	projectKey: string,
	gqlQuery: string,
) => {
	return useMemo(
		() =>
			withBoundary({
				Component: withSummaryData({
					Component: PriorityBreakdown,
					fetchDataFn: () => fetchPriorityBreakdownData(cloudId, projectKey, gqlQuery),
					FallbackComponent: MediumRetryWarning,
				}),
				boundaryId: BoundaryIds.PriorityBreakdown,
				Fallback: MediumContactSupportWarning,
				Skeleton: skeletons.PriorityBreakdown,
			}),
		[cloudId, projectKey, gqlQuery],
	);
};

export const useRecentActivityWithPaginatedData = (projectKey: string) => {
	const fetchActivityFeedDataWithProjectKey = useCallback(
		(after: string | null) => fetchActivityFeedData(after, projectKey),
		[projectKey],
	);

	return useMemo(
		() =>
			withBoundary({
				Component: withPaginatedSummaryData({
					Component: RecentActivity,
					fetchDataFn: fetchActivityFeedDataWithProjectKey,
					FallbackComponent: MediumRetryWarning,
					options: {
						fetchMode: 'replace',
					},
				}),
				boundaryId: BoundaryIds.RecentActivity,
				Fallback: MediumContactSupportWarning,
				Skeleton: skeletons.RecentActivity,
			}),

		[fetchActivityFeedDataWithProjectKey],
	);
};

export const useFastRecentActivityWithData = (projectId: string) => {
	return useMemo(
		() =>
			withBoundary({
				Component: withSummaryData({
					Component: ActivityFeed,
					fetchDataFn: () => fetchFastActivityFeedData(projectId),
					FallbackComponent: MediumRetryWarning,
				}),
				boundaryId: BoundaryIds.RecentActivity,
				Fallback: MediumContactSupportWarning,
				Skeleton: skeletons.RecentActivity,
			}),
		[projectId],
	);
};

export const useTypesOfWorkWithPaginatedData = (
	cloudId: string,
	projectKey: string,
	gqlQuery: string,
) => {
	const fetchTypesOfWorkDataWithProjectKey = useCallback(
		(after: string | null) => fetchTypesOfWorkData(after, cloudId, projectKey, gqlQuery),
		[cloudId, projectKey, gqlQuery],
	);
	return useMemo(
		() =>
			withBoundary({
				Component: withPaginatedSummaryData({
					Component: TypesOfWork,
					fetchDataFn: fetchTypesOfWorkDataWithProjectKey,
					FallbackComponent: MediumRetryWarning,
				}),
				boundaryId: BoundaryIds.TypesOfWork,
				Fallback: MediumContactSupportWarning,
				Skeleton: skeletons.TypeOfWork,
			}),
		[fetchTypesOfWorkDataWithProjectKey],
	);
};

export const useTeamWorkloadWithPaginatedData = (
	cloudId: string,
	projectKey: string,
	gqlQuery: string,
) => {
	const fetchTeamWorkloadDataWithProjectKey = useCallback(
		(after: string | null) => fetchTeamWorkloadData(after, cloudId, projectKey, gqlQuery),
		[cloudId, projectKey, gqlQuery],
	);
	return useMemo(
		() =>
			withBoundary({
				Component: withPaginatedSummaryData({
					Component: TeamWorkload,
					fetchDataFn: fetchTeamWorkloadDataWithProjectKey,
					FallbackComponent: MediumRetryWarning,
				}),
				boundaryId: BoundaryIds.TeamWorkload,
				Fallback: MediumContactSupportWarning,
				Skeleton: skeletons.TeamWorkload,
			}),
		[fetchTeamWorkloadDataWithProjectKey],
	);
};

export const useEpicProgressOrRelatedProjectsWithData = (
	relayEnvironment: Environment,
	cloudId: string,
	projectKey: string,
	projectId: string,
	projectType: JiraProjectType,
	atlassianAccountId: string | null,
	boundaryId: typeof BoundaryIds.EpicProgress | typeof BoundaryIds.RelatedProjects,
) => {
	const isBusinessProject = projectType === JIRA_PROJECT_TYPE_CORE_PROJECT;
	return useMemo(
		() =>
			isBusinessProject && fg('jwm-summary-page-toggle-related-projects')
				? withBoundary({
						Component: withSummaryData({
							Component: RelatedProjects,
							fetchDataFn: () => fetchRelatedProjectsData(cloudId, atlassianAccountId, projectId),
							FallbackComponent: MediumRetryWarning,
						}),
						boundaryId,
						Fallback: MediumContactSupportWarning,
						Skeleton: skeletons.RelatedProjects,
					})
				: withBoundary({
						Component: withSummaryData({
							Component: EpicProgress,
							fetchDataFn: () =>
								fetchEpicProgressData(relayEnvironment, cloudId, projectKey, projectId),
							FallbackComponent: MediumRetryWarning,
						}),
						boundaryId,
						Fallback: MediumContactSupportWarning,
						Skeleton: skeletons.EpicProgress,
					}),
		[
			isBusinessProject,
			boundaryId,
			cloudId,
			atlassianAccountId,
			projectId,
			relayEnvironment,
			projectKey,
		],
	);
};

export const useCsatOverTimeWithData = () => {
	return useMemo(
		() =>
			withBoundary({
				Component: withSummaryData({
					Component: CsatOverTime,
					fetchDataFn: () => fetchCsatOverTimeData(),
					FallbackComponent: MediumRetryWarning,
				}),
				boundaryId: BoundaryIds.CsatOverTime,
				Fallback: SmallContactSupportWarning,
				Skeleton: skeletons.CsatOverTime,
			}),
		[],
	);
};
